import LegalLayout from "./LegalLayout";

export default function Refund() {
  return (
    <LegalLayout title="Cancellation & Refund Policy" updated="10 February 2026">
      <p>
        This policy explains how cancellations, refunds, and disputes are handled for paid
        subscriptions on <strong>Anubandh AI</strong>. We believe in fair, transparent commerce.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">1. Nature of the Service</h2>
      <p>
        Anubandh AI provides a <strong>digital subscription service</strong> — AI-powered Marriage
        Intelligence Reports, curated match recommendations, and Kundali fusion analysis. Access
        is delivered instantly upon successful payment. No physical goods are shipped.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">2. Subscription Tiers</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Discovery:</strong> Free forever. No payment required.</li>
        <li><strong>Clarity:</strong> ₹1,999 per month (inclusive of GST).</li>
        <li><strong>Journey:</strong> ₹4,999 per month (inclusive of GST).</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">3. Cancellation</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>You can cancel your subscription <strong>anytime</strong> from the <em>Subscription</em> page or by emailing <a className="text-amber-700 underline" href="mailto:billing@anubandh.ai">billing@anubandh.ai</a>.</li>
        <li>Cancellation stops the next auto-renewal. You retain access until the end of your current paid billing cycle.</li>
        <li>There are no cancellation fees.</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">4. Refund Policy</h2>

      <h3 className="font-serif-display text-lg text-[#1E1B4B] mt-6">4.1 Full Refund — 7-Day Money-Back Guarantee</h3>
      <p>
        We offer a <strong>7-day money-back guarantee</strong> on your <strong>first paid subscription</strong>.
        If you are not satisfied within 7 days of the first payment, email
        <a className="text-amber-700 underline mx-1" href="mailto:billing@anubandh.ai">billing@anubandh.ai</a>
        with your registered email and reason. We will refund the full amount to your original
        payment method within <strong>5–7 business days</strong>. This applies only to the very first
        billing cycle.
      </p>

      <h3 className="font-serif-display text-lg text-[#1E1B4B] mt-6">4.2 Pro-Rated Refunds — Not Provided</h3>
      <p>
        After the 7-day window, subscription fees are <strong>non-refundable</strong>. Cancelling
        mid-cycle stops future renewals but does not entitle you to a partial refund for the
        remaining days of the current cycle. You continue to enjoy full access until the cycle ends.
      </p>

      <h3 className="font-serif-display text-lg text-[#1E1B4B] mt-6">4.3 Exceptional Refunds</h3>
      <p>Full or partial refunds may be granted at our discretion in the following situations:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Duplicate payment charged for the same subscription.</li>
        <li>Payment processed but subscription tier not activated within 24 hours.</li>
        <li>Technical inability of Anubandh AI to deliver the core service (verified by our team).</li>
        <li>Unauthorized transaction reported within 30 days (subject to Razorpay investigation).</li>
      </ul>

      <h3 className="font-serif-display text-lg text-[#1E1B4B] mt-6">4.4 What Is NOT Eligible for Refund</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Change of mind after the 7-day window.</li>
        <li>Not receiving a match or a marriage outcome — Anubandh AI is a decision-intelligence platform, not a matrimonial guarantee.</li>
        <li>Account suspension due to violation of our Terms &amp; Conditions.</li>
        <li>Disagreement with AI-generated report content, compatibility scores, or Kundali analysis.</li>
        <li>Downgrading to a lower tier mid-cycle (change takes effect from the next renewal).</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">5. How to Request a Refund</h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Email <a className="text-amber-700 underline" href="mailto:billing@anubandh.ai">billing@anubandh.ai</a> from your registered email address.</li>
        <li>Include: full name, registered mobile number, transaction ID (from Razorpay email), and reason for refund.</li>
        <li>Our billing team acknowledges within <strong>48 hours</strong>.</li>
        <li>Refund decisions are communicated within <strong>3 business days</strong>.</li>
        <li>Approved refunds are processed to your original payment method within <strong>5–7 business days</strong>.</li>
      </ol>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">6. Payment Gateway</h2>
      <p>
        All payments are processed by <strong>Razorpay Software Private Limited</strong>, a PCI-DSS
        compliant payment gateway. Refunds are subject to Razorpay&apos;s processing timelines.
        Anubandh AI does not store your card, UPI ID, or bank details.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">7. Chargebacks</h2>
      <p>
        If you initiate a chargeback with your bank or card network, please also notify us in
        parallel. Chargebacks initiated in bad faith (i.e., after using the service) may result in
        permanent account termination and legal recovery of the disputed amount.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">8. Grievance Escalation</h2>
      <p>
        If you are dissatisfied with a refund decision, you may escalate to our Grievance Officer:
      </p>
      <ul className="list-none pl-0 space-y-1">
        <li><strong>K. Singh</strong>, Grievance Officer</li>
        <li><strong>Email:</strong> <a className="text-amber-700 underline" href="mailto:grievance@anubandh.ai">grievance@anubandh.ai</a></li>
        <li><strong>Resolution SLA:</strong> 15 days from receipt.</li>
      </ul>

      <div className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500">
        This policy is aligned with Reserve Bank of India (RBI) payment guidelines and Razorpay
        merchant terms. It may be updated periodically; the &quot;Last updated&quot; date reflects the
        most recent revision.
      </div>
    </LegalLayout>
  );
}
